import type { MilestoneDay } from "@shared/schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  milestoneDay: MilestoneDay;
  value: string;
  onCommit: (next: string) => void;
  isOwner: boolean;
  variant?: "template" | "override";
  inheritedValue?: string;
  inheriting?: boolean;
  overrideBadgeLabel?: string | null;
  onRestoreOriginal?: () => void;
};

export function WelcomeMessageEditor({
  milestoneDay,
  value,
  onCommit,
  isOwner,
  variant = "template",
  inheritedValue = "",
  inheriting = false,
  overrideBadgeLabel = null,
  onRestoreOriginal,
}: Props) {
  const resolved = variant === "override" && inheriting ? inheritedValue : value;
  const [text, setText] = useState(resolved);
  const initialRef = useRef(resolved);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    setText(resolved);
    initialRef.current = resolved;
    autoResize();
  }, [resolved, autoResize]);

  function commit() {
    const trimmed = text.trim();
    if (trimmed === initialRef.current.trim()) return;
    onCommit(trimmed);
    initialRef.current = trimmed;
  }

  const isOverride = variant === "override";

  return (
    <section
      data-testid={`canvas-welcome-message-editor-D${milestoneDay}`}
      className="rounded-md border border-border bg-card/40"
    >
      <header className="px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold tracking-tight">Mensagem · Dia {milestoneDay}</h3>
          {isOverride && overrideBadgeLabel && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.02em] text-[hsl(var(--amber-state))]">
              {inheriting ? "Padrão do template" : `Customizado para ${overrideBadgeLabel}`}
            </span>
          )}
        </div>
        {isOverride && !inheriting && onRestoreOriginal && isOwner && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestoreOriginal}
            data-testid={`welcome-message-restore-D${milestoneDay}`}
          >
            Restaurar original
          </Button>
        )}
      </header>
      <div className="space-y-2 px-4 pb-3">
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autoResize();
          }}
          onBlur={commit}
          placeholder={`Mensagem opcional exibida ao colaborador no Dia ${milestoneDay}.`}
          rows={3}
          maxLength={2000}
          disabled={!isOwner}
          className="resize-none overflow-hidden bg-secondary"
          data-testid={`welcome-message-input-D${milestoneDay}`}
        />
        <p className="text-[11px] text-muted-foreground">
          {isOverride
            ? "Personalize só para este colaborador. Vazio = usar mensagem do template."
            : "Personalizável por colaborador no momento de iniciar o onboarding."}
        </p>
      </div>
    </section>
  );
}
